"use client";

// Faction-select UI sounds (added 2026-09-11 from the team's "UI SFX" drop;
// the prototype had no audio). Files live under /public/faction/sfx.
//   hover.mp3            0.3 s blip on card hover / focus
//   confirm.mp3          1.0 s confirm on click
//   announce-<key>.mp3   3.1–3.7 s voice line ("Enter The Divine Realm"…)
// Browsers refuse audio before the first click/tap on the page, so the hover
// blip is silent on a cold visit until the user has interacted once; play()
// rejections are swallowed so nothing else is affected.

export type FactionKey = "angel" | "demon" | "mortal" | "security" | "ronin";
export const ANNOUNCE_CAP_MS = 4000; // never hold navigation longer than this

const cache = new Map<string, HTMLAudioElement>();
const get = (name: string) => {
  let a = cache.get(name);
  if (!a) { a = new Audio(`/faction/sfx/${name}.mp3`); a.preload = "auto"; cache.set(name, a); }
  return a;
};
const play = (a: HTMLAudioElement, volume = 1) => {
  try { a.pause(); a.currentTime = 0; a.volume = volume; const p = a.play(); if (p) p.catch(() => {}); } catch { /* ignore */ }
};

/** Warm the cache so the first hover/click doesn't wait on a fetch. */
export function preloadFactionSounds() {
  if (typeof window === "undefined") return;
  ["hover", "confirm", "announce-angel", "announce-demon", "announce-mortal", "announce-security", "announce-ronin"].forEach(get);
}

let lastHover = 0;
export function playHover() {
  const now = performance.now();
  if (now - lastHover < 120) return; // sliding between cards shouldn't machine-gun it
  lastHover = now;
  play(get("hover"), 0.6);
}

/** Confirm blip + the faction's voice line; resolves when the line ends (or at the cap). */
export function playSelection(faction: FactionKey): Promise<void> {
  play(get("confirm"), 0.8);
  const line = get(`announce-${faction}`);
  return new Promise((resolve) => {
    let done = false;
    const finish = () => { if (!done) { done = true; line.removeEventListener("ended", finish); resolve(); } };
    line.addEventListener("ended", finish);
    window.setTimeout(finish, ANNOUNCE_CAP_MS);
    try {
      line.pause(); line.currentTime = 0; line.volume = 1;
      const p = line.play();
      if (p) p.catch(() => finish()); // blocked or missing → don't hold the click hostage
    } catch { finish(); }
  });
}
